"use client";

import { DateTime } from "luxon";
import React from "react";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";
import { EventEntity } from "@features/events/types/event";
import { EventCard } from "@features/events/ui/event-card";
import { WorkspaceEntity } from "@features/workspaces/types/workspace";

// ============================================================================
// УТИЛИТЫ ДЛЯ СОЗДАНИЯ ТЕСТОВЫХ ДАННЫХ
// ============================================================================

const createDate = (daysOffset: number = 0): Date => {
  return DateTime.now().plus({ days: daysOffset }).toJSDate();
};

const createFixedDate = (year: number, month: number, day: number): Date => {
  return DateTime.fromObject({ year, month, day }).toJSDate();
};

const createISODate = (year: number, month: number, day: number): string => {
  return DateTime.fromObject({ year, month, day }).toISO()!;
};

const createBaseEvent = (overrides: Partial<EventEntity> = {}): EventEntity => ({
  id: 1,
  createdAt: createDate(-60),
  workspaceId: "ws-1",
  projectId: "proj-1",
  hint: "TestEvent",
  content: {
    status: "published",
    data: {
      title: { ru: "Тестовое Событие", en: "Test Event" },
      description: { ru: "Описание события", en: "Event description" },
      brandColors: { iv: ["#FF6B6B", "#4ECDC4"] },
      coloredLogo: { ru: [], en: [] },
      logoUri: { iv: "https://placehold.co/100x100?text=Logo" },
      monochromeLogo: { ru: [], en: [] },
      playlistUri: { iv: "" },
      siteUri: { ru: "https://example.com", en: "https://example.com" },
      options: {
        iv: {
          isDatesDetermined: true,
          isDatesApproved: true,
          isOnCorporateSite: true,
          presentationsFilteringMode: "show_for_all",
        },
      },
      activityLinkPattern: { ru: "", en: "" },
      cfpUri: { ru: "", en: "" },
      personLinkPattern: { ru: "", en: "" },
      srmId: { iv: "" },
      srmProjectId: { iv: "" },
      srmVersion: { iv: "" },
    },
  },
  rights: {
    canRead: true,
    canPatch: true,
    canDelete: false,
    canArchive: true,
    canListMembers: true,
    canAddMember: false,
  },
  uiLinks: {
    edit: "/events/1/edit",
    team: "/events/1/team",
  },
  modulesRights: {
    canViewScheduleModule: true,
    canViewScheduleRegularModule: true,
  },
  ...overrides,
});

// ============================================================================
// МОКИ СОБЫТИЙ
// ============================================================================

const mockWorkspace: WorkspaceEntity = {
  id: "ws-1",
  createdAt: createDate(-90),
  hint: "Рабочее пространство",
  content: {
    status: "published",
    data: {
      title: { ru: "Рабочее пространство", en: "Workspace" },
      description: { ru: "Описание рабочего пространства", en: "Workspace description" },
    },
  },
  rights: {
    canRead: true,
    canPatch: true,
    canDelete: false,
    canListMembers: true,
    canAddMember: false,
  },
  uiLinks: {
    edit: "/workspaces/ws-1/edit",
    team: "/workspaces/ws-1/team",
  },
};

const fullEvent = createBaseEvent({
  id: 101,
  createdAt: createFixedDate(2025, 1, 1),
  stage: "PROGRESSING",
  objectStatus: "active",
  dates: {
    status: "PUBLISHED",
    timezone: "Europe/Moscow",
    days: {
      [createISODate(2025, 3, 15)]: "OFFLINE",
      [createISODate(2025, 3, 16)]: "OFFLINE",
      [createISODate(2025, 3, 17)]: "ONLINE",
    },
    intervals: [],
    source: "manual",
    total: {
      lowerBoundary: createISODate(2025, 3, 15),
      upperBoundary: createISODate(2025, 3, 17),
    },
  },
});

const minimalEvent = createBaseEvent({
  id: 102,
  createdAt: createDate(-30),
  stage: "UNKNOWN",
  objectStatus: "active",
  content: {
    status: "published",
    data: {
      title: { ru: "Минимальное Событие", en: "Minimal Event" },
      description: { ru: "", en: "" },
      brandColors: { iv: [] },
      coloredLogo: { ru: [], en: [] },
      logoUri: { iv: "" },
      monochromeLogo: { ru: [], en: [] },
      playlistUri: { iv: "" },
      siteUri: { ru: "", en: "" },
      options: {
        iv: {
          isDatesDetermined: false,
          isDatesApproved: false,
          isOnCorporateSite: false,
          presentationsFilteringMode: "show_for_all",
        },
      },
      activityLinkPattern: { ru: "", en: "" },
      cfpUri: { ru: "", en: "" },
      personLinkPattern: { ru: "", en: "" },
      srmId: { iv: "" },
      srmProjectId: { iv: "" },
      srmVersion: { iv: "" },
    },
  },
});

const eventWithoutLogo = createBaseEvent({
  id: 103,
  createdAt: createDate(-45),
  stage: "PLANNING",
  objectStatus: "active",
  content: {
    status: "published",
    data: {
      title: { ru: "Событие Без Логотипа", en: "Event Without Logo" },
      description: { ru: "Описание события без логотипа", en: "Event description without logo" },
      brandColors: { iv: [] },
      coloredLogo: { ru: [], en: [] },
      logoUri: { iv: "" },
      monochromeLogo: { ru: [], en: [] },
      playlistUri: { iv: "" },
      siteUri: { ru: "https://example.com", en: "https://example.com" },
      options: {
        iv: {
          isDatesDetermined: true,
          isDatesApproved: true,
          isOnCorporateSite: true,
          presentationsFilteringMode: "show_for_all",
        },
      },
      activityLinkPattern: { ru: "", en: "" },
      cfpUri: { ru: "", en: "" },
      personLinkPattern: { ru: "", en: "" },
      srmId: { iv: "" },
      srmProjectId: { iv: "" },
      srmVersion: { iv: "" },
    },
  },
});

const completedEvent = createBaseEvent({
  id: 104,
  createdAt: createFixedDate(2025, 2, 1),
  stage: "COMPLETED",
  objectStatus: "archived",
  dates: {
    status: "PUBLISHED",
    timezone: "America/New_York",
    days: {
      [createISODate(2025, 6, 10)]: "HYBRID",
      [createISODate(2025, 6, 11)]: "HYBRID",
    },
    intervals: [],
    source: "manual",
    total: {
      lowerBoundary: createISODate(2025, 6, 10),
      upperBoundary: createISODate(2025, 6, 11),
    },
  },
});

const preparingEvent = createBaseEvent({
  id: 105,
  createdAt: createDate(-20),
  stage: "PREPARING",
  objectStatus: "active",
  datesAlt: {
    hint: { ru: "Примерные даты", en: "Approximate dates" },
    days: {
      [createISODate(2025, 9, 5)]: "OFFLINE",
      [createISODate(2025, 9, 6)]: "OFFLINE",
    },
  },
});

// 1) Документация свойств публичного API
const generalProps: PropertyDefinition[] = [
  {
    name: "event",
    type: "EventEntity",
    defaultValue: "undefined",
    description:
      "Доменная сущность события. Используются поля content.data.title, content.data.description, content.data.logoUri.iv, stage, dates, datesAlt, workspaceId, modulesRights и другие для отображения информации и определения доступных действий.",
  },
  {
    name: "mode",
    type: '"status-page" | "dashboard"',
    defaultValue: "undefined (required)",
    description:
      "Режим отображения карточки. В режиме 'dashboard' карточка имеет эффект hover и заголовок становится ссылкой при наличии workspaceId.",
  },
  {
    name: "showDashboardLink",
    type: "boolean",
    defaultValue: "false",
    description:
      "Флаг для отображения кнопки 'Просмотр деталей' в нижней части карточки. Также активирует ссылку на заголовке в режиме 'dashboard'.",
  },
  {
    name: "workspace",
    type: "WorkspaceEntity | null",
    defaultValue: "undefined",
    description:
      "Объект рабочего пространства. Используется для построения меню действий и отображения информации о рабочем пространстве в футере карточки.",
  },
  {
    name: "showWorkspaceInfo",
    type: "boolean",
    defaultValue: "undefined",
    description:
      "Флаг для отображения информации о рабочем пространстве в футере карточки. Работает только если передан workspace.",
  },
  {
    name: "venueName",
    type: "string",
    defaultValue: "undefined",
    description: "Название площадки для отображения в информации о событии.",
  },
  {
    name: "seasonName",
    type: "string",
    defaultValue: "undefined",
    description: "Название сезона для отображения в информации о событии.",
  },
  {
    name: "className",
    type: "string",
    defaultValue: "undefined",
    description: "Дополнительные CSS-классы для контейнера карточки.",
  },
];

const EventCardFixture = () => (
  <>
    {/* 1. Описание */}
    <FixtureSection title="1. Описание" id="about">
      <div className="space-y-4">
        <p>
          Карточка события <strong>EventCard</strong> отображает ключевую информацию о событии:
          название, описание, даты, стадию, локацию, сезон, сайт и логотип. Компонент также
          предоставляет действия для редактирования события, просмотра деталей и редактирования
          расписания. Компонент адаптируется под разные контексты использования (статус события,
          дашборд) и корректно деградирует при неполных данных.
        </p>
        <p>
          Когда использовать: на странице статуса события для отображения основной информации, на
          дашборде для отображения списка событий, в виджетах и других местах, где требуется
          компактное представление события.
        </p>
        <p>
          <strong>Ключевые особенности:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Локализация заголовка, описания и текстов статусов через i18n-хуки.</li>
          <li>
            Приоритетная загрузка логотипа события с автоматическим фоллбэком на паттерн фона при
            отсутствии изображения.
          </li>
          <li>Контейнерные запросы Tailwind для адаптивной типографики внутри карточки.</li>
          <li>
            Автоматическое определение доступных действий на основе прав пользователя и статуса
            события.
          </li>
          <li>Аккуратная обработка отсутствующих данных с отображением Нет данных.</li>
          <li>
            Ссылка на название события ведет на страницу статуса события при наличии workspaceId.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 2. Meta */}
    <FixtureSection title="2. Meta" id="meta">
      <FixtureMetadata
        componentName="EventCard"
        sourcePath="src/features/events/ui/event-card.tsx"
        domain="Events (управление событиями)"
        dependencies={[
          {
            path: "@features/events/types/event",
            description: "тип доменной сущности события",
          },
          {
            path: "@components/ui/card",
            description: "UI-примитивы карточки (shadcn/ui)",
          },
          {
            path: "@components/ui/custom/fade-failsafe-image",
            description: "защищённая загрузка изображений",
          },
          {
            path: "@components/common/buttons/entity-edit-or-view-link",
            description: "универсальная ссылка действия редактирования/просмотра",
          },
          {
            path: "@components/common/content/entity-dashboard-link",
            description: "ссылка на дашборд события",
          },
          {
            path: "@features/events/ui/event-dates-range",
            description: "компонент отображения диапазона дат события",
          },
          {
            path: "@features/events/ui/event-site-link",
            description: "ссылка на сайт события",
          },
          {
            path: "@features/routes",
            description: "маршруты приложения",
          },
        ]}
      />
    </FixtureSection>

    {/* 3. Бизнес-контекст */}
    <FixtureSection title="3. Бизнес-контекст" id="business-context">
      <div className="space-y-4">
        <p>
          <strong>Роли и сценарии использования:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Организатор события:</strong> просмотр основной информации о событии на странице
            статуса. Ожидаемая ценность: быстрый обзор ключевых данных события и доступные действия.
          </li>
          <li>
            <strong>Администратор рабочего пространства:</strong> обзор всех событий в дашборде.
            Ожидаемая ценность: визуальная идентификация событий и быстрый переход к деталям.
          </li>
          <li>
            <strong>Контент-менеджер:</strong> управление событиями и их расписанием. Ожидаемая
            ценность: доступ к редактированию контента и расписания.
          </li>
        </ul>
        <p>
          <strong>Ключевые ограничения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Компонент отображает действия только при наличии соответствующих прав (
            <code>canPatch</code> для редактирования).
          </li>
          <li>
            Ссылка на редактор расписания отображается только при наличии{" "}
            <code>
              modulesRights.canViewScheduleModule и modulesRights.canViewScheduleRegularModule
            </code>
            .
          </li>
          <li>
            Название события является ссылкой только при наличии <code>workspaceId</code>.
          </li>
          <li>
            Весь текстовый контент локализован через <code>next-intl</code>.
          </li>
          <li>
            Навигация использует Next.js App Router <code>Link</code> компонент.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 4. Пропсы и типы */}
    <FixtureSection title="4. Пропсы и типы" id="props-types">
      <FixturePropsTable properties={generalProps} />
      <FixturePropertyImpactTable
        properties={[
          {
            propertyPath: "event.content.data.title",
            type: "LocaleMessageType",
            renderImpact: "Основной заголовок карточки",
            missingBehavior: "Используется event.hint или пустая строка",
          },
          {
            propertyPath: "event.content.data.description",
            type: "LocaleMessageType",
            renderImpact: "Описание события под заголовком",
            missingBehavior: 'Отображается "Нет данных"',
          },
          {
            propertyPath: "event.content.data.logoUri.iv",
            type: "string",
            renderImpact: "Логотип события в шапке карточки",
            missingBehavior: "Отображается фоновый паттерн без изображения",
          },
          {
            propertyPath: "event.stage",
            type: "EventStage",
            renderImpact: "Бейдж стадии и описание в заголовке",
            missingBehavior: 'Отображается стадия "UNKNOWN"',
          },
          {
            propertyPath: "event.dates",
            type: "EventDates",
            renderImpact: "Даты события в блоке информации",
            missingBehavior: "Отображаются альтернативные даты или пустое поле",
          },
          {
            propertyPath: "event.datesAlt",
            type: "EventDatesAlt",
            renderImpact: "Альтернативные даты события при отсутствии основных",
            missingBehavior: "Скрыто при наличии основных дат",
          },
          {
            propertyPath: "event.content.data.siteUri",
            type: "LocaleMessageType",
            renderImpact: "Ссылка на сайт события",
            missingBehavior: 'Отображается "Нет данных"',
          },
          {
            propertyPath: "mode",
            type: '"status-page" | "dashboard"',
            renderImpact: "Стили карточки и поведение заголовка",
            missingBehavior: "Обязательный параметр, должен быть указан",
          },
          {
            propertyPath: "showDashboardLink",
            type: "boolean",
            renderImpact: "Отображение кнопки просмотра деталей и активация ссылки на заголовке",
            missingBehavior: "Скрыто при false",
          },
          {
            propertyPath: "workspace",
            type: "WorkspaceEntity | null",
            renderImpact:
              "Объект рабочего пространства для построения меню действий и отображения информации",
            missingBehavior: "Меню действий строится без контекста рабочего пространства",
          },
          {
            propertyPath: "showWorkspaceInfo",
            type: "boolean",
            renderImpact: "Отображение информации о рабочем пространстве в футере",
            missingBehavior: "Скрыто при отсутствии флага или workspace",
          },
          {
            propertyPath: "venueName",
            type: "string",
            renderImpact: "Название площадки события",
            missingBehavior: 'Отображается "Нет данных" при отсутствии',
          },
          {
            propertyPath: "seasonName",
            type: "string",
            renderImpact: "Название сезона события",
            missingBehavior: 'Отображается "Нет данных" при отсутствии',
          },
        ]}
      />
    </FixtureSection>

    {/* 5. Инварианты */}
    <FixtureSection title="5. Инварианты" id="invariants">
      {/* 5.1 Базовый */}
      <FixtureSection title="5.1 Базовый" id="invariant-base-render" variant="inner">
        <FixtureInvariantMeta
          id="base-render"
          name="Базовая карточка события с полными данными"
          goal="Проверка стандартного рендеринга со всеми заполненными данными и логотипом события."
          preconditions="Все обязательные и опциональные поля заполнены корректно."
          inputData={[
            "event.content.data.title = локализованная строка",
            "event.content.data.description = локализованная строка",
            "event.content.data.logoUri.iv = валидный URL",
            'event.stage = "PROGRESSING"',
            "event.dates = заполнены",
            "event.content.data.siteUri = локализованная строка",
          ]}
          expectedResult='Карточка отображает заголовок, описание, логотип события, даты, стадию "В процессе", ссылку на сайт и кнопки действий.'
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "event.content.data.title",
              type: "LocaleMessageType",
              value: '{ ru: "Тестовое Событие", en: "Test Event" }',
              description: "Локализованный заголовок",
            },
            {
              name: "event.content.data.description",
              type: "LocaleMessageType",
              value: '{ ru: "Описание события", en: "Event description" }',
              description: "Локализованное описание",
            },
            {
              name: "event.content.data.logoUri.iv",
              type: "string",
              value: "URL",
              description: "Логотип события",
            },
            {
              name: "event.stage",
              type: "EventStage",
              value: '"PROGRESSING"',
              description: "Стадия события",
            },
            {
              name: "event.dates",
              type: "EventDates",
              value: "заполнены",
              description: "Даты события",
            },
          ]}
        />
        <FixtureWrapper label="Базовый" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={fullEvent} mode="status-page" />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.2 Минимальные данные */}
      <FixtureSection title="5.2 Минимальные данные" id="invariant-minimal-data" variant="inner">
        <FixtureInvariantMeta
          id="minimal-data"
          name="Карточка события с минимальными данными"
          goal="Проверка отображения карточки с минимально необходимыми данными."
          preconditions="Заполнены только обязательные поля, остальные данные отсутствуют."
          inputData={[
            "event.content.data.title = локализованная строка",
            'event.content.data.description = ""',
            "event.content.data.logoUri.iv = пустая строка",
            'event.stage = "UNKNOWN"',
            "event.dates = undefined",
            'event.content.data.siteUri = ""',
          ]}
          expectedResult='Карточка отображается с заголовком, пустым описанием ("Нет данных"), фоновым паттерном вместо логотипа, стадией "Неизвестно", пустыми полями дат и сайта.'
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "event.content.data.title",
              type: "LocaleMessageType",
              value: '{ ru: "Минимальное Событие", en: "Minimal Event" }',
              description: "Локализованный заголовок",
            },
            {
              name: "event.content.data.description",
              type: "LocaleMessageType",
              value: '{ ru: "", en: "" }',
              description: "Пустое описание",
            },
            {
              name: "event.content.data.logoUri.iv",
              type: "string",
              value: '""',
              description: "Пустой логотип",
            },
            {
              name: "event.stage",
              type: "EventStage",
              value: '"UNKNOWN"',
              description: "Неизвестная стадия",
            },
            {
              name: "event.dates",
              type: "EventDates",
              value: "undefined",
              description: "Отсутствуют даты",
            },
          ]}
        />
        <FixtureWrapper label="Минимальные данные" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={minimalEvent} mode="status-page" />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.3 Без логотипа */}
      <FixtureSection title="5.3 Без логотипа" id="invariant-no-logo" variant="inner">
        <FixtureInvariantMeta
          id="no-logo"
          name="Карточка события без логотипа"
          goal="Проверка отображения карточки при отсутствии логотипа события."
          preconditions="Логотип события отсутствует, остальные данные заполнены."
          inputData={[
            "event.content.data.title = локализованная строка",
            "event.content.data.logoUri.iv = пустая строка",
          ]}
          expectedResult="Карточка отображается с фоновым паттерном вместо логотипа, остальные данные отображаются корректно."
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "event.content.data.title",
              type: "LocaleMessageType",
              value: '{ ru: "Событие Без Логотипа", en: "Event Without Logo" }',
              description: "Локализованный заголовок",
            },
            {
              name: "event.content.data.logoUri.iv",
              type: "string",
              value: '""',
              description: "Пустой логотип",
            },
          ]}
        />
        <FixtureWrapper label="Без логотипа" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={eventWithoutLogo} mode="status-page" />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.4 Различные стадии */}
      <FixtureSection title="5.4 Различные стадии" id="invariant-stages" variant="inner">
        <FixtureInvariantMeta
          id="stages"
          name="Галерея различных стадий события"
          goal="Проверка отображения различных стадий события."
          preconditions="Нет."
          inputData={[
            'Стадия 1: event.stage = "UNKNOWN"',
            'Стадия 2: event.stage = "PLANNING"',
            'Стадия 3: event.stage = "PREPARING"',
            'Стадия 4: event.stage = "PROGRESSING"',
            'Стадия 5: event.stage = "COMPLETED"',
          ]}
          expectedResult="Каждая карточка корректно отображает соответствующую стадию события с локализованным названием и бейджем."
          edgeCases="Нет."
        />
        <FixtureWrapper label="Стадии" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={minimalEvent} mode="status-page" />
            <EventCard event={preparingEvent} mode="status-page" />
            <EventCard event={fullEvent} mode="status-page" />
            <EventCard event={completedEvent} mode="status-page" />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.5 С датами и без */}
      <FixtureSection title="5.5 С датами и без" id="invariant-dates" variant="inner">
        <FixtureInvariantMeta
          id="dates"
          name="Отображение дат события"
          goal="Проверка корректного отображения различных вариантов дат события."
          preconditions="Нет."
          inputData={[
            "Состояние 1: event.dates = заполнены (публикованные даты)",
            "Состояние 2: event.datesAlt = заполнены (предварительные даты)",
            "Состояние 3: event.dates = undefined и event.datesAlt = undefined (нет дат)",
          ]}
          expectedResult="Корректное отображение публикованных дат, предварительных дат или 'Нет данных' при их отсутствии."
          edgeCases="Нет."
        />
        <FixtureWrapper label="Даты" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={fullEvent} mode="status-page" />
            <EventCard event={preparingEvent} mode="status-page" />
            <EventCard event={minimalEvent} mode="status-page" />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.6 Для дашборда */}
      <FixtureSection title="5.6 Для дашборда" id="invariant-dashboard" variant="inner">
        <FixtureInvariantMeta
          id="dashboard"
          name="Карточка события для дашборда"
          goal="Проверка отображения карточки в контексте дашборда с дополнительной информацией."
          preconditions="Компонент используется на дашборде с флагами mode='dashboard' и showDashboardLink."
          inputData={[
            'mode = "dashboard"',
            "showDashboardLink = true",
            "workspace = WorkspaceEntity",
            "showWorkspaceInfo = true",
            'venueName = "Конференц-зал"',
            'seasonName = "Весна 2025"',
          ]}
          expectedResult="Карточка отображает эффект hover, заголовок становится ссылкой, дополнительную информацию о рабочем пространстве и кнопку просмотра деталей."
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "mode",
              type: '"dashboard"',
              value: '"dashboard"',
              description: "Режим дашборда",
            },
            {
              name: "showDashboardLink",
              type: "boolean",
              value: "true",
              description: "Показывать кнопку просмотра деталей",
            },
            {
              name: "workspace",
              type: "WorkspaceEntity",
              value: "mockWorkspace",
              description: "Объект рабочего пространства",
            },
            {
              name: "showWorkspaceInfo",
              type: "boolean",
              value: "true",
              description: "Показывать информацию о рабочем пространстве",
            },
            {
              name: "venueName",
              type: "string",
              value: '"Конференц-зал"',
              description: "Название площадки",
            },
            {
              name: "seasonName",
              type: "string",
              value: '"Весна 2025"',
              description: "Название сезона",
            },
          ]}
        />
        <FixtureWrapper label="Дашборд" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard
              event={fullEvent}
              mode="dashboard"
              showDashboardLink={true}
              workspace={mockWorkspace}
              showWorkspaceInfo={true}
              venueName="Конференц-зал"
              seasonName="Весна 2025"
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.7 С действиями */}
      <FixtureSection title="5.7 С действиями" id="invariant-actions" variant="inner">
        <FixtureInvariantMeta
          id="actions"
          name="Доступные действия в карточке"
          goal="Проверка отображения различных действий в зависимости от прав пользователя."
          preconditions="Нет."
          inputData={[
            "Права на редактирование есть → отображается кнопка редактирования",
            "Права на редактирование расписания есть → отображается кнопка редактирования расписания",
            "Флаг showDashboardLink=true → отображается кнопка просмотра деталей",
          ]}
          expectedResult="Корректное отображение доступных действий в зависимости от прав пользователя и контекста использования."
          edgeCases="Нет."
        />
        <FixtureWrapper label="Действия" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <EventCard event={fullEvent} mode="dashboard" showDashboardLink={true} />
          </div>
        </FixtureWrapper>
      </FixtureSection>
    </FixtureSection>

    {/* 6. Примеры и edge-cases */}
    <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
      <div className="space-y-4">
        <p>
          <strong>Полнота данных:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Полные данные:</strong> Все поля заполнены → полноценный рендер с логотипом и
            всеми данными.
          </li>
          <li>
            <strong>Минимальные данные:</strong> Только обязательные поля → карточка рендерится без
            опциональных элементов.
          </li>
          <li>
            <strong>Отсутствует логотип:</strong> event.content.data.logoUri.iv = → отображается
            фоновый паттерн.
          </li>
          <li>
            <strong>Очень длинное описание:</strong> 200+ символов → адаптивная типографика, перенос
            текста.
          </li>
        </ul>
        <p>
          <strong>Специальные символы:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Эмодзи в заголовке и описании должны корректно отображаться.</li>
          <li>HTML-сущности должны быть экранированы.</li>
        </ul>
      </div>
    </FixtureSection>

    {/* 7. Accessibility */}
    <FixtureSection title="7. Доступность" id="accessibility">
      <ul className="ml-6 list-disc space-y-1">
        <li>Ссылки доступны с клавиатуры (Tab, Enter/Space).</li>
        <li>Атрибут alt логотипа использует event.hint или заголовок события.</li>
        <li>Фоновые паттерны помечены aria-hidden.</li>
        <li>Контрастность соответствует WCAG AA.</li>
        <li>
          Кнопки действий имеют понятные aria-label при необходимости (редактирование, просмотр и
          т.д.).
        </li>
      </ul>
    </FixtureSection>

    {/* 8. FixtureLibsUsed */}
    <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
      <FixtureLibsUsed
        items={[
          { label: "React", href: "https://react.dev/" },
          {
            label: "Next.js (Link)",
            href: "https://nextjs.org/docs/app/api-reference/components/link",
          },
          { label: "next-intl", href: "https://next-intl-docs.vercel.app/" },
          { label: "shadcn/ui — Card", href: "https://ui.shadcn.com/docs/components/card" },
          { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          { label: "clsx", href: "https://github.com/lukeed/clsx" },
          { label: "Luxon", href: "https://moment.github.io/luxon/#/" },
        ]}
      />
    </FixtureSection>

    {/* 9. Рекомендации */}
    <FixtureSection title="9. Рекомендации" id="guidelines">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Передавайте валидный <code>event</code> объект с минимально необходимыми данными.
        </li>
        <li>
          Указывайте <code>mode=&quot;dashboard&quot;</code> для дашборда и{" "}
          <code>mode=&quot;status-page&quot;</code> для страницы статуса.
        </li>
        <li>
          Используйте <code>workspace</code> и <code>showWorkspaceInfo</code> для отображения
          контекста рабочего пространства на дашборде.
        </li>
        <li>
          Устанавливайте <code>showDashboardLink</code> в true для отображения кнопки просмотра
          деталей и активации ссылки на заголовке.
        </li>
        <li>Для длинных заголовков опирайтесь на контейнерные запросы и ограничения ширины.</li>
        <li>Логотип события опционален; отсутствие логотипа безопасно.</li>
      </ul>
    </FixtureSection>

    {/* 10. Бизнес-правила */}
    <FixtureSection title="10. Бизнес-правила" id="business-rules">
      <div className="space-y-3">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Инвариант 1 (минимальный): компонент отображается при наличии{" "}
            <code>event.content.data.title</code> или <code>event.hint</code>.
          </li>
          <li>
            Инвариант 2 (приоритет изображений): если <code>event.content.data.logoUri.iv</code>{" "}
            содержит валидный URL, отображается логотип события. Если логотип отсутствует,
            отображается фоновый паттерн.
          </li>
          <li>
            Инвариант 3 (локализация): заголовок, описание и статусы берутся через i18n-утилиты.
            Текущая локаль определяется через <code>useLocale()</code>.
          </li>
          <li>
            Инвариант 4 (стадии): отображается бейдж стадии с локализованным названием. При
            отсутствии стадии отображается Неизвестно.
          </li>
          <li>
            Инвариант 5 (даты): если <code>event.dates</code> заполнены, отображаются публикованные
            даты. Если <code>event.dates</code> отсутствуют, но есть <code>event.datesAlt</code>,
            отображаются предварительные даты. Если оба поля отсутствуют, отображается Нет данных.
          </li>
          <li>
            Инвариант 6 (действия): кнопка редактирования отображается при{" "}
            <code>event.rights.canPatch = true</code>. Кнопка редактирования расписания отображается
            при{" "}
            <code>
              event.modulesRights.canViewScheduleModule = true и
              event.modulesRights.canViewScheduleRegularModule = true
            </code>
            . Кнопка просмотра деталей отображается при <code>showDashboardLink = true</code>.
          </li>
          <li>
            Инвариант 7 (навигация): ссылка на название события ведет на страницу статуса события
            при наличии <code>event.workspaceId</code> и <code>showDashboardLink = true</code>.
          </li>
          <li>
            Инвариант 8 (режимы): при <code>mode = &quot;dashboard&quot;</code> карточка имеет
            эффект hover и заголовок становится ссылкой (при наличии workspaceId и
            showDashboardLink=true).
          </li>
          <li>
            Инвариант 9 (адаптив): типографика заголовка масштабируется через container queries
            классы <code>@[200px]/card</code>, <code>@[250px]/card</code>.
          </li>
          <li>
            Инвариант 10 (отсутствующие данные): при отсутствии описания, площадки, сезона или сайта
            отображается Нет данных.
          </li>
          <li>
            Инвариант 11 (рабочее пространство): информация о рабочем пространстве отображается в
            футере при передаче <code>workspace</code> и <code>showWorkspaceInfo = true</code>.
            Также <code>workspace</code> используется для построения меню действий (eventSubLinks).
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 11. Примечания/допущения */}
    <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
      <div className="space-y-3">
        <p>
          <strong>Контекстуальные допущения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            Логика прав: <code>event.rights.canPatch</code> определяет доступность редактирования.
          </li>
          <li>
            Логика модулей: <code>event.modulesRights.canViewScheduleEditor</code> определяет
            доступность редактирования расписания.
          </li>
          <li>Локализация: все статусы и тексты имеют переводы в соответствующих файлах.</li>
          <li>Container queries: поддержка включена в Tailwind конфигурации.</li>
          <li>
            Приоритет данных: основные даты → предварительные даты → Нет данных. Логотип → фоновый
            паттерн.
          </li>
        </ul>
        <p>
          <strong>TODO/риски:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Подтвердить наличие переводов для всех стадий события.</li>
          <li>Проверить контрастность WCAG AA для всех элементов в тёмной теме.</li>
          <li>
            Проверить корректность отображения логотипов различных соотношений сторон с{" "}
            <code>object-cover</code>.
          </li>
        </ul>
      </div>
    </FixtureSection>
  </>
);

export default EventCardFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Events",
  parentItem: "Cards",
  title: "Event Card",
  description: "Карточка события: название, описание, даты, статус и действия",
};
